// components/shared/ErrorBoundary.tsx
import React from 'react';
import { autoRecoverDynamicImportOnce, isDynamicImportError } from '../../core/recoverDynamicImport';

interface Props {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error('ErrorBoundary caught:', error, info);
    void autoRecoverDynamicImportOnce(error, () => window.location.reload());
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) return this.props.fallback;
      return (
        <div className="min-h-screen bg-gray-950 text-white flex items-center justify-center">
          <div className="bg-gray-900 rounded-lg p-6 border border-red-900 max-w-md text-center">
            <div className="text-red-400 text-lg font-bold mb-2">
              {isDynamicImportError(this.state.error) ? '页面资源已更新或暂不可用' : 'Something went wrong'}
            </div>
            <div className="text-gray-400 text-xs font-mono">
              {this.state.error?.message}
            </div>
            {isDynamicImportError(this.state.error) && (
              <p className="mt-2 text-sm text-gray-300">请重新加载最新页面；若网络仍不可用，请稍后重试。</p>
            )}
            <button
              onClick={() => window.location.reload()}
              className="mt-4 px-4 py-1.5 bg-gray-800 hover:bg-gray-700 rounded text-sm"
            >
              {isDynamicImportError(this.state.error) ? '重新加载 / Retry' : 'Retry'}
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
